"use client"

import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
  type ReactNode,
} from "react"

// ──────────────────────────────────────────────────────────────────────────────
// Popover — zero-dependency, accessible floating panel anchored below a trigger.
//
// Knows nothing about "help" or any specific content — it just toggles a panel
// open/closed and wires the standard overlay affordances:
//   • click the trigger to toggle
//   • Escape, click-outside, or clicking a link inside (navigation) closes it
//   • focus moves into the panel on open and returns to the trigger on close
//
// Placement is intentionally fixed (directly below the trigger). No flip /
// collision engine — YAGNI for the admin help-dot use case.
//
// Trigger wiring: the caller passes a SINGLE interactive element (e.g.
// <IconButton aria-label="…">) as `trigger`. We clone it to inject the toggle
// onClick plus `aria-expanded` / `aria-haspopup`, so there's exactly one real
// <button> — no nested interactive elements. IconButton (and Button) spread
// unknown props onto the underlying <button>, so onClick + aria-* land on it.
// Note: IconButton is not a forwardRef component, so we do NOT rely on a cloned
// `ref` reaching the button — focus-return queries the live button inside the
// wrapper instead.
// ──────────────────────────────────────────────────────────────────────────────

export interface PopoverProps {
  /**
   * The interactive element that toggles the panel. Must accept `onClick` and
   * `aria-*` props on its underlying button (IconButton / Button both do). The
   * Popover owns this element's `aria-expanded` and `aria-haspopup="dialog"`.
   */
  trigger: ReactElement
  /** Accessible name for the floating panel (`aria-label` on `role="dialog"`). */
  label: string
  /** Panel contents. */
  children: ReactNode
  /**
   * Which edge of the trigger the panel aligns to; use `'right'` for triggers
   * near the right edge so the panel grows leftward and stays on-screen.
   * Defaults to `'left'`.
   */
  align?: "left" | "right"
  /** Extra classes for the floating panel. */
  className?: string
}

// Props we inject onto the cloned trigger.
type TriggerInjectedProps = {
  onClick: (e: ReactMouseEvent<HTMLElement>) => void
  "aria-expanded": boolean
  "aria-haspopup": "dialog"
}

export function Popover({ trigger, label, children, align = "left", className = "" }: PopoverProps) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLSpanElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const panelId = useId()

  const close = useCallback(() => setOpen(false), [])

  // Return focus to the trigger button when closing. IconButton doesn't forward
  // a ref, so we query the live <button> inside the wrapper (it's the only
  // interactive element outside the panel).
  const closeAndRestoreFocus = useCallback(() => {
    setOpen(false)
    // Safe even while the panel is still mounted: the trigger precedes the panel in DOM order, so pre-order querySelector returns the trigger (not a panel child).
    const btn = wrapperRef.current?.querySelector<HTMLElement>(
      'button, [role="button"]'
    )
    btn?.focus()
  }, [])

  // ── Escape + click-outside (only while open) ──────────────────────────────
  useEffect(() => {
    if (!open) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation()
        closeAndRestoreFocus()
      }
    }

    const onMouseDown = (e: globalThis.MouseEvent) => {
      const wrapper = wrapperRef.current
      if (wrapper && e.target instanceof Node && !wrapper.contains(e.target)) {
        close()
      }
    }

    document.addEventListener("keydown", onKeyDown)
    document.addEventListener("mousedown", onMouseDown)
    return () => {
      document.removeEventListener("keydown", onKeyDown)
      document.removeEventListener("mousedown", onMouseDown)
    }
  }, [open, close, closeAndRestoreFocus])

  // ── Move focus into the panel when it opens ───────────────────────────────
  useEffect(() => {
    if (open) panelRef.current?.focus()
  }, [open])

  const onTriggerClick = useCallback((e: ReactMouseEvent<HTMLElement>) => {
    // Preserve any onClick the caller already put on the trigger.
    const original = (trigger.props as { onClick?: (ev: ReactMouseEvent<HTMLElement>) => void })
      .onClick
    original?.(e)
    setOpen((v) => !v)
  }, [trigger])

  // Close on navigation: any click that lands on (or inside) an <a> dismisses
  // the panel so it doesn't linger over the next view.
  const onPanelClick = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    if (e.target instanceof Element && e.target.closest("a")) {
      close()
    }
  }, [close])

  const triggerEl = isValidElement(trigger)
    ? cloneElement(trigger as ReactElement<TriggerInjectedProps>, {
        onClick: onTriggerClick,
        "aria-expanded": open,
        "aria-haspopup": "dialog",
      })
    : trigger

  return (
    <span ref={wrapperRef} className="relative inline-flex">
      {triggerEl}
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label={label}
          tabIndex={-1}
          onClick={onPanelClick}
          className={[
            "absolute top-full mt-2 z-50",
            align === "right" ? "right-0" : "left-0",
            "max-w-[min(320px,calc(100vw-2rem))]",
            "rounded-xl border border-[var(--redy-border)]",
            "bg-[var(--redy-surface)] text-[var(--redy-charcoal)]",
            "[box-shadow:var(--shadow-card-hover)]",
            "p-4 focus:outline-none",
            className,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {children}
        </div>
      )}
    </span>
  )
}
